import { A, useNavigate } from "@solidjs/router";
import { createSignal, type JSX, Show } from "solid-js";
import { ComingSoonToast } from "../components/ComingSoon";
import { MenuDrawer } from "../components/MenuDrawer";
import { Spinner } from "../components/Spinner";
import { Stories } from "../components/Stories";
import { dismissPromo, promoDismissed } from "../state/promo";
import { useResumeOngoingTrip } from "../state/resume";
import { session } from "../state/session";
import { markStorySeen } from "../stories";

/** Figma 01 Home: the M'Ticket home with the "Vélo" tile leading to the Pony map. */
export function Home() {
  const navigate = useNavigate();
  const me = session.me;
  const [promo, setPromo] = createSignal(!promoDismissed());
  const [menu, setMenu] = createSignal(false);
  const [parkingStories, setParkingStories] = createSignal(false);
  useResumeOngoingTrip();
  return (
    <main class="screen bg-[#f8f8fa]">
      <header
        class="flex items-center justify-between pl-[10px] pr-[11px] pt-2"
        style={{ "padding-top": "max(0.5rem, env(safe-area-inset-top))" }}
      >
        <Show
          when={!me.loading}
          fallback={
            <span class="flex h-11 items-center px-[6px]" role="status">
              <Spinner size={18} class="text-violet" />
              <span class="sr-only">Chargement du compte</span>
            </span>
          }
        >
          <A
            href={me() ? "/account" : "/signin"}
            class="flex h-11 min-w-[160px] items-center gap-2 px-[6px] text-sm font-medium leading-[1.25] tracking-[0.14px] text-strong"
          >
            <span class="relative h-7 w-7 shrink-0">
              <img src="/icons/person-ring-28.svg" width="28" height="28" alt="" />
              <img
                src="/icons/person.svg"
                width="20"
                height="20"
                alt=""
                class="absolute left-1 top-1"
              />
            </span>
            <span class="truncate max-w-[220px]">
              {me() ? me()?.firstName || me()?.email : "Se connecter"}
            </span>
          </A>
        </Show>
        <button
          type="button"
          class="h-[50px] w-[50px] rounded-full bg-violet flex items-center justify-center"
          aria-label="Menu"
          aria-expanded={menu()}
          onClick={() => setMenu(true)}
        >
          <img src="/icons/menu.svg" width="26" height="26" alt="" />
        </button>
      </header>

      <div class="px-[15px] pb-4 short:pb-3">
        <div class="relative mx-[18px] mt-[42px] h-12 short:mt-4">
          {/* Itinerary search needs an account: /recherche sends signed-out riders to sign in first. */}
          <button
            type="button"
            class="w-full h-12 rounded-full border-[1.2px] border-black bg-[#e4deea] pl-[25px] pr-14 flex items-center text-left text-[15px] leading-[1.25] tracking-[0.15px] text-strong"
            onClick={() => navigate("/recherche")}
          >
            Où voulez-vous aller ?
          </button>
          <span class="absolute right-0 top-0 h-12 w-12">
            <img src="/icons/search-circle.svg" width="48" height="48" alt="" />
            <img
              src="/icons/search-white.svg"
              width="26"
              height="26"
              alt=""
              class="absolute left-[11px] top-[11px]"
            />
          </span>
        </div>

        <div class="mx-auto mt-[11px] flex w-[306px] max-w-full flex-col gap-[11px]">
          <div class="relative h-[53px] rounded-[18px] bg-violet text-white">
            <img
              src="/icons/bell-white.svg"
              width="26"
              height="26"
              alt=""
              class="absolute left-5 top-[13.5px]"
            />
            <div class="absolute left-[58px] top-[6px] text-base font-medium leading-[1.25] tracking-[0.16px]">
              Mes infos trafic
            </div>
            <div class="absolute left-[58px] top-[28px] text-[10.5px] font-light leading-[1.25] tracking-[0.105px]">
              Gérer mes alertes
            </div>
          </div>
          <div class="relative h-[89px] rounded-[18px] bg-violet text-white">
            <img
              src="/icons/qr-scan-white-30.svg"
              width="30"
              height="30"
              alt=""
              class="absolute left-[18px] top-[29.5px]"
            />
            <div class="absolute left-[58px] top-[9px] w-[230px] text-base font-medium leading-[1.25] tracking-[0.16px]">
              Acheter & voir mes tickets et abonnements
            </div>
            <div class="absolute left-[58px] top-[53px] w-[220px] text-[10.5px] font-light leading-[1.25] tracking-[0.105px]">
              Voir mon Pass gratuité et mes e-tickets en cours
            </div>
          </div>
        </div>

        <Show when={promo()}>
          <div class="relative mx-auto mt-[31px] flex h-[145px] w-[338px] max-w-full flex-col items-center rounded-[40px] bg-[#e4deea] text-[15px] leading-[1.25] tracking-[0.15px] text-text short:mt-4">
            <div class="mt-[23px]">Vous êtes résident de la Métropole ?</div>
            <div class="mt-[11px] font-semibold">Demandez votre Pass gratuité</div>
            <button
              type="button"
              class="mt-4 h-10 w-[150px] rounded-[20px] bg-violet text-base font-medium leading-[1.25] text-white"
            >
              Obtenir
            </button>
            <button
              type="button"
              class="absolute -right-[8px] -top-[20px] h-11 w-11 flex items-center justify-center"
              aria-label="Masquer"
              onClick={() => {
                dismissPromo();
                setPromo(false);
              }}
            >
              <span class="relative h-[26px] w-[26px]">
                <img src="/icons/dismiss-circle-violet.svg" width="26" height="26" alt="" />
                <img
                  src="/icons/close-white-16.svg"
                  width="16"
                  height="16"
                  alt=""
                  class="absolute left-[5px] top-[5px]"
                />
              </span>
            </button>
          </div>
        </Show>
      </div>

      <nav
        class="mt-auto shrink-0 grid grid-cols-3 gap-y-[29px] rounded-t-[24px] bg-white px-2 pt-[22px] text-center short:gap-y-4 short:pt-4"
        style={{ "padding-bottom": "max(1.25rem, env(safe-area-inset-bottom))" }}
      >
        {/* Mode tiles from the Figma "01 Home" frame: M'Ticket images, and the Pony bike tile. */}
        <Tile label="Voyager">
          <img src="/modes/voyager.png" alt="" width="64" height="64" />
        </Tile>
        <Tile label="Vélo" badge="Nouveau" onClick={() => navigate("/map")}>
          <span class="h-16 w-16 rounded-full bg-indigo flex items-center justify-center">
            <img src="/icons/bike-white-36.svg" width="36" height="36" alt="" />
          </span>
        </Tile>
        {/* The Pony parking stories (Z1–Z3), as from the ride's "Zones et stationnement". */}
        <Tile label="Stationner" onClick={() => setParkingStories(true)}>
          <img src="/modes/stationner.png" alt="" width="64" height="64" />
        </Tile>
        <Tile label="P+R">
          <img src="/modes/pr.png" alt="" width="64" height="64" />
        </Tile>
        <Tile label="Vélobox">
          <img src="/modes/velobox.png" alt="" width="64" height="64" />
        </Tile>
        <Tile label="Recharge">
          <img src="/modes/recharge.png" alt="" width="64" height="64" />
        </Tile>
      </nav>
      <MenuDrawer open={menu()} onClose={() => setMenu(false)} />
      <Show when={parkingStories()}>
        <Stories
          set="zones"
          onClose={() => setParkingStories(false)}
          onDone={() => {
            markStorySeen("zones");
            setParkingStories(false);
          }}
        />
      </Show>
      <ComingSoonToast />
    </main>
  );
}

function Tile(props: {
  label: string;
  badge?: string;
  onClick?: () => void;
  children: JSX.Element;
}) {
  return (
    <button
      type="button"
      class="relative mx-auto flex flex-col items-center gap-1"
      onClick={props.onClick}
    >
      <span class="h-16 w-16 rounded-full overflow-hidden flex items-center justify-center">
        {props.children}
      </span>
      {props.badge && (
        <span class="absolute left-[53px] -top-[9px] h-[18px] w-[54px] rounded-[9px] bg-indigo text-[10px] font-medium leading-[18px] tracking-[0.1px] text-white">
          {props.badge}
        </span>
      )}
      <span class="text-[11px] leading-[1.25] tracking-[0.11px] text-text">{props.label}</span>
    </button>
  );
}
